import { automationAccountsEn } from '../features/automation/i18n/accounts';
import { automationCaseLinksEn } from '../features/automation/i18n/case-links';
import { automationCatalogEn } from '../features/automation/i18n/catalog';
import { automationProjectEn } from '../features/automation/i18n/project';
import { automationDeploysEn } from '../features/automation/i18n/deploys';
import { automationRunsEn } from '../features/automation/i18n/runs';
import { automationScheduleEn } from '../features/automation/i18n/schedule';
import { coverageEn } from '../features/projects/coverage-i18n';
import { runAutotestsEn } from '../features/test-runs/autotests-i18n';
import type { Message } from './types';

/**
 * The English dictionary is the source of truth for keys: `MessageKey` is
 * derived from it, and the Russian dictionary is typed against it, so neither
 * can drift from the other.
 */
export const en = {
  // ─── shared vocabulary ────────────────────────────────────────────────────
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.duplicate': 'Duplicate',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.retry': 'Try again',
  'common.search': 'Search',
  'common.filters': 'Filters',
  'common.resetFilters': 'Reset filters',
  'common.sort': 'Sort',
  'common.copyLink': 'Copy a link to this record',
  'common.copyLinkTo': 'Copy a link to {key}',
  'common.linkCopied': 'Link copied',
  'common.linkCopyFailed': 'Could not copy the link',
  'common.andJoin': ' and ',
  'common.clearSearch': 'Clear search',
  'common.none': 'None',
  'common.empty': '—',
  'common.unassigned': 'Unassigned',
  'common.loading': 'Loading',
  'common.moreActions': 'More actions',
  'common.discard': 'Discard',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.status': 'Status',
  'common.created': 'Created',
  'common.updated': 'Updated',
  'common.author': 'Author',
  'common.name': 'Name',
  'common.done': 'Done',
  'common.title': 'Title',
  'common.description': 'Description',
  'common.folder': 'Folder',
  'common.environment': 'Environment',
  'common.layer': 'Layer',
  'common.module': 'Module',
  'common.comment': 'Comment',
  'common.progress': 'Progress',
  'common.somethingWentWrong': 'Something went wrong',
  'common.skipToContent': 'Skip to content',
  'common.appTagline': 'Test Management System',
  'common.pagination': 'Pagination',
  'common.paginationRange': '{from}–{to} of {total}',
  'common.paginationPage': '{page} / {totalPages}',
  'common.previousPage': 'Previous page',
  'common.nextPage': 'Next page',
  'common.optional': 'optional',

  // ─── navigation ───────────────────────────────────────────────────────────
  'nav.main': 'Main navigation',
  'nav.dashboard': 'Dashboard',
  'nav.projects': 'Projects',
  'nav.automation': 'Automation',
  'nav.users': 'Users',
  'nav.overview': 'Overview',
  'nav.testCases': 'Test Cases',
  'nav.checklists': 'Checklists',
  'nav.testRuns': 'Test Runs',
  'nav.settings': 'Settings',
  'nav.projectSections': 'Project sections',
  'nav.searchProject': 'Search this project',
  'nav.signOut': 'Sign out',
  'nav.appearance': 'Appearance',
  'nav.language': 'Language',
  'nav.themeSystem': 'System',
  'nav.themeLight': 'Light',
  'nav.themeDark': 'Dark',

  // ─── authentication ───────────────────────────────────────────────────────
  'auth.signInWithGitlab': 'Sign in with GitLab',
  'auth.gitlabHint': 'Sign in with your company GitLab account.',
  'auth.gitlab.denied': 'GitLab sign-in was cancelled.',
  'auth.gitlab.state': 'The sign-in link has expired. Try again.',
  'auth.gitlab.exchange': 'GitLab did not confirm the sign-in. Try again.',
  'auth.gitlab.profile': 'Could not read your GitLab profile, or this login belongs to another GitLab account.',
  'auth.gitlab.not_member': 'Your GitLab account has no access to TMS. Ask an administrator to add you.',
  'auth.gitlab.inactive': 'Your TMS account is deactivated. Ask an administrator.',
  'auth.gitlab.disabled': 'GitLab sign-in is not configured on this server.',
  'auth.gitlab.failed': 'GitLab sign-in failed. Try again later.',

  // ─── errors ───────────────────────────────────────────────────────────────
  'error.loadFailed': 'Could not load this page',
  'error.pageNotFound': 'Page not found',
  'error.pageNotFoundHint':
    'The page you are looking for does not exist, or the project was renamed or deleted.',
  'error.backToDashboard': 'Back to dashboard',
  'error.renderFailed': 'The page could not be displayed.',
  'error.reload': 'Reload the page',
  'error.network': 'Cannot reach the server. Check your connection and try again.',
  'error.unexpectedResponse': 'Unexpected server response. Please try again.',
  'error.internal': 'Something went wrong. Please try again.',
  'error.unauthenticated': 'Your session has expired. Please sign in again.',
  'error.forbidden': 'You do not have permission to perform this action',
  'error.notFound': 'This record no longer exists',
  'error.rateLimited': 'Too many requests. Please try again later.',

  // ─── enumerations ─────────────────────────────────────────────────────────
  'enum.role.admin': 'Admin',
  'enum.role.qa_lead': 'QA Lead',
  'enum.role.qa_engineer': 'QA Engineer',
  'enum.role.analyst': 'Analyst',
  'enum.role.viewer': 'Viewer',
  'enum.projectStatus.active': 'Active',
  'enum.projectStatus.archived': 'Archived',
  'enum.priority.low': 'Low',
  'enum.priority.medium': 'Medium',
  'enum.priority.high': 'High',
  'enum.priority.critical': 'Critical',
  'enum.severity.trivial': 'Trivial',
  'enum.severity.minor': 'Minor',
  'enum.severity.major': 'Major',
  'enum.severity.critical': 'Critical',
  'enum.severity.blocker': 'Blocker',
  'enum.caseStatus.draft': 'Draft',
  'enum.caseStatus.active': 'Active',
  'enum.caseStatus.deprecated': 'Deprecated',
  'enum.runStatus.active': 'In Progress',
  'enum.runStatus.completed': 'Completed',
  'enum.runStatus.aborted': 'Aborted',
  'enum.itemStatus.not_run': 'Not Run',
  'enum.itemStatus.passed': 'Passed',
  'enum.itemStatus.failed': 'Failed',
  'enum.itemStatus.blocked': 'Blocked',
  'enum.itemStatus.skipped': 'Skipped',
  'enum.priorityLabel': 'Priority',
  'enum.automated.yes': 'Yes',
  'enum.automated.no': 'No',
  'enum.labelColor.gray': 'Grey',
  'enum.labelColor.blue': 'Blue',
  'enum.labelColor.green': 'Green',
  'enum.labelColor.amber': 'Amber',
  'enum.labelColor.red': 'Red',
  'enum.labelColor.purple': 'Purple',
  'enum.projectIcon.folder': 'Folder',
  'enum.projectIcon.layers': 'Layers',
  'enum.projectIcon.globe': 'Globe',
  'enum.projectIcon.smartphone': 'Phone',
  'enum.projectIcon.monitor': 'Monitor',
  'enum.projectIcon.server': 'Server',
  'enum.projectIcon.database': 'Database',
  'enum.projectIcon.cloud': 'Cloud',
  'enum.projectIcon.shield': 'Shield',
  'enum.projectIcon.lock': 'Lock',
  'enum.projectIcon.card': 'Card',
  'enum.projectIcon.wallet': 'Wallet',
  'enum.projectIcon.chart': 'Chart',
  'enum.projectIcon.bot': 'Robot',
  'enum.projectIcon.package': 'Box',
  'enum.projectIcon.beaker': 'Beaker',
  'enum.projectIcon.briefcase': 'Briefcase',
  'enum.projectIcon.users': 'People',
  'enum.projectIcon.settings': 'Gear',
  'enum.projectIcon.sparkles': 'Sparkles',
  'enum.severityLabel': 'Severity',

  // ─── dashboard ────────────────────────────────────────────────────────────
  'dashboard.welcome': 'Welcome back, {name}',
  'dashboard.subtitle': 'Everything your QA team is working on right now.',
  'dashboard.statProjects': 'Projects',
  'dashboard.statProjectsHint': '{count} active',
  'dashboard.statTestCases': 'Test cases',
  'dashboard.statChecklists': 'Checklists',
  'dashboard.statTestRuns': 'Test runs',
  'dashboard.statActiveRuns': 'Active runs',
  'dashboard.activeRuns': 'Active test runs',
  'dashboard.activeRunsHint': 'Runs that are still being executed.',
  'dashboard.allProjects': 'All projects',
  'dashboard.noActiveRuns': 'No active test runs',
  'dashboard.noActiveRunsHint': 'Open a project and start a run from one of its checklists.',
  'dashboard.latestRuns': 'Latest test runs',
  'dashboard.recentCases': 'Recently created test cases',
  'dashboard.noRuns': 'No test runs yet',
  'dashboard.noCases': 'No test cases yet',
  'dashboard.noCasesHint': 'Create a project and add your first test case.',
  'dashboard.columnRun': 'Run',
  'dashboard.columnEnvironment': 'Environment',
  'dashboard.columnAssignee': 'Assignee',
  'dashboard.noFolder': 'No folder',
  'dashboard.passedShort': '{count}P',
  'dashboard.failedShort': '{count}F',

  // ─── automation (TestRun) ─────────────────────────────────────────────────
  ...automationProjectEn,
  ...automationRunsEn,
  ...automationAccountsEn,
  ...automationCaseLinksEn,
  ...automationScheduleEn,
  ...automationDeploysEn,
  ...coverageEn,
  ...automationCatalogEn,
  ...runAutotestsEn,
  'automation.title': 'Automation',
  'automation.subtitle': 'Team autotests: GitLab runs, results and test data. Test accounts stay in TestRun.',
  'automation.role': 'In TestRun you work as {role}',
  'automation.statTeams': 'Teams',
  'automation.statProjects': 'Projects',
  'automation.statTests': 'Autotests',
  'automation.statActiveRuns': 'Running now',
  'automation.activeRuns': 'Running now',
  'automation.activeRunsHint': 'Pipelines still going in GitLab. Refreshed every 15 seconds.',
  'automation.noActiveRuns': 'Nothing is running',
  'automation.columnProject': 'Project',
  'automation.columnBranch': 'Branch',
  'automation.columnStartedBy': 'Started by',
  'automation.columnStarted': 'Started',
  'automation.teams': 'Teams',
  'automation.projectTests': { one: '{count} test', other: '{count} tests' },
  'automation.noProjects': 'No projects yet',
  'automation.lastRun': 'Last run {when}',
  'automation.neverRun': 'Not run yet',
  'automation.unreachable':
    'TestRun cannot be reached. Check the network, or open TestRun in a new tab — Cloudflare may be asking to confirm you are not a robot.',
  'automation.empty': 'No autotests in TMS yet',
  'automation.emptyHint': 'An administrator brings them over from TestRun.',
  'automation.emptyImportHint': 'Bring teams, projects and test data over from TestRun in one go.',
  'automation.import': 'Bring over from TestRun',
  'automation.importTitle': 'Bring data over from TestRun?',
  'automation.importDescription':
    'Teams, projects, the test catalogue, test data, class notes and run history in TMS are replaced with TestRun data. Test accounts stay in TestRun. Can be repeated until every page has moved.',
  'automation.importDone': 'Brought over: {teams} teams, {projects} projects, {classes} classes, {runs} runs',
  'automation.status.pending': 'Queued',
  'automation.status.running': 'Running',
  'automation.status.success': 'Passed',
  'automation.status.failed': 'Failed',
  'automation.status.cancelled': 'Cancelled',
  'automation.connection.open': 'Connection',
  'automation.connection.title': 'Connection to TestRun and GitLab',
  'automation.connection.hint':
    'Works without cluster secrets: TMS keeps its own key for TestRun, and the GitLab token is saved here. Secrets set in the cluster take precedence.',
  'automation.connection.testRun': 'TestRun (test accounts)',
  'automation.connection.testRunUrl': 'TestRun address: {url}',
  'automation.connection.testRunOff': 'TestRun address is not set (AUTOMATION_API_URL) — the accounts part is off.',
  'automation.connection.reachCheck': 'Check the TMS server reaches TestRun',
  'automation.connection.reachOk': 'The TMS server reaches TestRun: {status} in {ms} ms — a scheduled run can take pool accounts.',
  'automation.connection.reach.not-set': 'The TestRun address is not set.',
  'automation.connection.reach.timeout': 'No answer from TestRun in 8 s — the network from DEV to stage looks closed.',
  'automation.connection.reach.dns': 'The TMS server cannot resolve the TestRun address (DNS).',
  'automation.connection.reach.refused': 'The connection to TestRun was refused or the network is unreachable.',
  'automation.connection.reach.tls': 'TLS failed: the TMS server does not accept the TestRun certificate.',
  'automation.connection.reach.cloudflare': 'Cloudflare in front of TestRun refused the TMS server ({status}).',
  'automation.connection.reach.http': 'TestRun answered {status}.',
  'automation.connection.reach.network': 'Network error on the way to TestRun.',
  'automation.connection.reachDetail': 'For DevOps: {detail}',
  'automation.connection.publicKeyHint':
    'TestRun checks that a user came from TMS with this public key. Put the line into TestRun settings (helm/stage-values.yaml). It is not a secret.',
  'automation.connection.keyFromTms': 'The key was created by TMS and is kept in its database.',
  'automation.connection.keyFromSecret': 'The key comes from the cluster secret AUTOMATION_SIGNING_KEY.',
  'automation.connection.copy': 'Copy',
  'automation.connection.copied': 'Copied',
  'automation.connection.gitlab': 'GitLab token',
  'automation.connection.gitlabHint':
    'TMS uses it to start pipelines, read their results and clone repositories for a scan ({url}). The api scope is needed; the group token TestRun uses will do.',
  'automation.connection.tokenSet': 'set',
  'automation.connection.tokenMissing': 'not set',
  'automation.connection.tokenFromSecret': 'The token comes from the cluster secret AUTOMATION_GITLAB_TOKEN and is changed there.',
  'automation.connection.tokenSetBy': 'Saved by {who}, {when}. It is never shown again.',
  'automation.connection.tokenLabel': 'Token',
  'automation.connection.replace': 'Replace',
  'automation.connection.remove': 'Remove',
  'automation.connection.tokenSaved': 'GitLab accepted the token; it is saved',
  'automation.connection.tokenRemoved': 'Token removed',
  'automation.notice': 'Note',
  'automation.trigger.push': 'on push',
  'automation.trigger.schedule': 'on schedule',
  'automation.trigger.web': 'from GitLab',
  'automation.trigger.merge_request_event': 'on MR',
  'automation.trigger.trigger': 'trigger',
  'automation.trigger.hint': 'GitLab started this pipeline itself; it was not started from here',
  'automation.cause.title': 'Why it failed',
  'automation.cause.byText': 'The cause is read from the error text',
  'automation.cause.allOwner': { one: 'The {count} red test is for {owner} to look into.', other: 'All {count} are for {owner} to look into.' },
  'automation.cause.owners': 'Who should look: {list}.',
  'automation.cause.more': { one: 'and {count} more — all on the run page', other: 'and {count} more — all on the run page' },
  'automation.cause.product': 'Product defect',
  'automation.cause.product.hint': 'The test reached its check and it did not hold',
  'automation.cause.stand': 'Stand unavailable',
  'automation.cause.stand.hint': 'A service did not answer: timeout, 502/503/504, circuit breaker, maintenance',
  'automation.cause.login': 'Login and OTP',
  'automation.cause.login.hint': 'No OTP, or not found in Kafdrop; send limit (429); biometry block',
  'automation.cause.access': 'No access 401/403',
  'automation.cause.access.hint': 'The test expected success, the service refused the test account',
  'automation.cause.autotest': 'Autotest or data',
  'automation.cause.autotest.hint': 'The test broke before its check, or the account lacked data',
  'automation.scope.all': 'All tests',
  'automation.scope.suite': 'Suite: {suite}',
  'automation.scope.classes': { one: '{count} class', other: '{count} classes' },
  'automation.history.empty': 'No runs yet.',
  'automation.history.id': 'ID',
  'automation.history.scope': 'What ran',
  'automation.history.env': 'Env',
  'automation.history.status': 'Status',
  'automation.history.report': 'Report',
  'automation.history.reportShort': 'Report',
  'automation.history.gradleReport': 'Gradle HTML report (Allure was not built)',

  // ─── projects ─────────────────────────────────────────────────────────────
  'projects.title': 'Projects',
  'projects.subtitle': 'Each project holds its own test documentation and run history.',
  'projects.new': 'New project',
  'projects.create': 'Create project',
  'projects.edit': 'Edit project',
  'projects.searchPlaceholder': 'Search by name or code',
  'projects.searchLabel': 'Search projects',
  'projects.filterStatus': 'Filter by status',
  'projects.allStatuses': 'All statuses',
  'projects.columnKey': 'Key',
  'projects.columnProject': 'Project',
  'projects.columnCases': 'Cases',
  'projects.columnChecklists': 'Checklists',
  'projects.columnRuns': 'Runs',
  'projects.columnLastActivity': 'Last activity',
  'projects.activeRunsSuffix': '({count} active)',
  'projects.empty': 'No projects yet',
  'projects.emptyHint': 'Create your first project to start writing test documentation.',
  'projects.emptyFiltered': 'No projects match your filters',
  'projects.emptyFilteredHint': 'Try a different search term or clear the status filter.',
  'projects.formHint': 'A project holds its own folders, test cases, checklists and test runs.',
  'projects.icon': 'Icon',
  'projects.iconColor': 'Icon colour',
  'projects.fieldCode': 'Code',
  'projects.fieldCodeHint': 'Short identifier used in keys, e.g. PAY → PAY-1, PAY-CHK-1, PAY-RUN-1.',
  'projects.created': 'Project {code} created',
  'projects.updated': 'Project updated',
  'projects.archive': 'Archive project',
  'projects.restore': 'Restore project',
  'projects.archived': 'Project {code} archived',
  'projects.restored': 'Project {code} restored',
  'projects.statusChangeFailed': 'Could not change the project status',
  'projects.saveFailed': 'Could not save the project',
  'projects.deleted': 'Project {code} deleted',
  'projects.deleteFailed': 'Could not delete the project',
  'projects.deleteAction': 'Delete project',
  'projects.deleteTitle': 'Delete {name}?',
  'projects.deleteHint':
    'The project and all of its folders, test cases, checklists and test runs will no longer be accessible. This cannot be undone from the interface.',
  'projects.deleteHintCounts':
    '{cases}, {checklists} and {runs} will no longer be accessible. This cannot be undone from the interface.',
  'projects.countCases': { one: '{count} test case', other: '{count} test cases' } as Message,
  'projects.countChecklists': { one: '{count} checklist', other: '{count} checklists' } as Message,
  'projects.countRuns': { one: '{count} test run', other: '{count} test runs' } as Message,

  // ─── project overview & settings ──────────────────────────────────────────
  'overview.title': 'Overview',
  'overview.newCase': 'New test case',
  'overview.newChecklist': 'New checklist',
  'overview.newRun': 'New test run',
  'overview.latestResult': 'Latest run result',
  'overview.openRun': 'Open run',
  'overview.progressSummary': '{progress}% done · {passRate}% passed',
  'overview.recentRuns': 'Recent test runs',
  'overview.recentCases': 'Recent test cases',
  'overview.viewAll': 'View all',
  'overview.noRuns': 'No test runs yet',
  'overview.noRunsHint': 'Start a run from a checklist.',
  'overview.noCases': 'No test cases yet',
  'overview.noCasesHint': 'Add your first test case.',
  'overview.noEnvironment': 'No environment',
  'settings.title': 'Settings',
  'settings.subtitle': 'Project details and lifecycle.',
  'settings.details': 'Details',
  'settings.lifecycle': 'Lifecycle',
  'settings.lifecycleHint':
    'Archiving hides the project from the active list but keeps all of its data.',

  // ─── folders ──────────────────────────────────────────────────────────────
  'folders.title': 'Folders',
  'folders.new': 'New folder',
  'folders.create': 'Create folder',
  'folders.editTitle': 'Rename or move folder',
  'folders.newSubfolder': 'New subfolder',
  'folders.rename': 'Rename or move',
  'folders.delete': 'Delete folder',
  'folders.parent': 'Parent folder',
  'folders.noParent': 'No parent (top level)',
  'folders.allChecklists': 'All checklists',
  'folders.allRuns': 'All runs',
  'folders.allCases': 'All test cases',
  'folders.created': 'Folder created',
  'folders.updated': 'Folder updated',
  'folders.deleted': 'Folder deleted',
  'folders.saveFailed': 'Could not save the folder',
  'folders.deleteFailed': 'Could not delete the folder',
  'folders.empty': 'No folders yet. Group test cases by feature to keep them findable.',
  'folders.emptyChecklists': 'No folders yet. Group checklists by feature to keep them findable.',
  'folders.emptyRuns': 'No folders yet. Group runs by release or feature to keep them findable.',
  'folders.deleteTitle': 'Delete folder "{name}"?',
  'folders.deleteHint': 'The folder and its subfolders will be deleted.',
  'folders.deleteHintCases':
    'This folder and its subfolders contain {content}. Choose where to keep it, or delete it with the folder.',
  'folders.deleteHintOnlyFolder':
    'This folder and its subfolders contain {content}, and there is nowhere else to put it — it will be deleted with the folder.',
  'folders.deleteBlockedRuns':
    'This folder and its subfolders hold {content}. A run is kept as history, so it cannot be deleted with the folder — move it to another folder first.',
  'folders.deleteMoveTo': 'Move the contents to',
  'folders.deleteMoveCases': 'Move and delete the folder',
  'folders.deleteWithCases': 'Delete with the contents',
  'folders.collapse': 'Collapse {name}',
  'folders.expand': 'Expand {name}',

  // ─── test cases ───────────────────────────────────────────────────────────
  'cases.title': 'Test cases',
  'cases.subtitle': 'The detailed test documentation of this project.',
  'cases.new': 'New test case',
  'cases.import': 'Import',
  'cases.importTitle': 'Import test cases',
  'cases.importHint':
    'A CSV or Excel (XLSX) export from another system — BrowserStack, TestRail, Zephyr, Qase or a spreadsheet of your own.',
  'cases.importNoFile': 'No file chosen',
  'cases.importPick': 'Choose a file',
  'cases.importAnotherFile': 'Another file',
  'cases.importFile_empty': 'The file has a header but no data rows.',
  'cases.importFile_tooLarge': 'The file is larger than 5 MB.',
  'cases.importFile_unsupported': 'Supported formats are CSV and XLSX.',
  'cases.importFile_unreadable': 'The file could not be read.',
  'cases.importStepsLayout': 'Steps in the file',
  'cases.importStepsRows': 'One row per step',
  'cases.importStepsCell': 'A list in one cell',
  'cases.importColumnNone': 'Do not import',
  'cases.importColumnUnnamed': 'Column {index}',
  'cases.importFieldTitle': 'Title',
  'cases.importFieldDescription': 'Description',
  'cases.importFieldPreconditions': 'Precondition',
  'cases.importFieldTestData': 'Test data',
  'cases.importFieldStepAction': 'Step',
  'cases.importFieldStepExpected': 'Expected result',
  'cases.importFieldGroupKey': 'Key of the source system (groups rows)',
  'cases.importFieldPriority': 'Priority',
  'cases.importFieldLabels': 'Labels',
  'cases.importFieldEnvironment': 'Environment',
  'cases.importFieldLayer': 'Layer',
  'cases.importFieldModule': 'Module',
  'cases.importNoTitle': 'Choose the column holding the title',
  'cases.importPreview': 'Ready to import: {count}',
  'cases.importStepCount': 'Steps',
  'cases.importMissingTitle': 'Without a title — will be skipped',
  'cases.importEmptyResult': 'No test cases came out of this mapping.',
  'cases.importTooMany': 'One import takes up to {max} test cases. Split the file.',
  'cases.importDefaults':
    'The key, the author and the dates are assigned here, and so are status Draft and severity Minor. '
    + 'Priority, labels, environment, layer and module are read from the file when it has them, and names '
    + 'this project does not know yet are added to it.',
  'cases.importSubmit': 'Import',
  'cases.importDone': 'Imported: {count}',
  'cases.importSkipped': 'Skipped: {count}',
  'cases.importRow': 'Row',
  'cases.importReason': 'Reason',
  'cases.importReport': 'Download the report',
  'cases.importCreatedLabels': 'Labels added to the project: {names}',
  'cases.importCreatedEnvironments': 'Environments added to the project: {names}',
  'cases.importCreatedLayers': 'Layers added to the project: {names}',
  'cases.importCreatedModules': 'Modules added to the project: {names}',
  'cases.importFailed': 'Could not import the test cases',
  'cases.create': 'Create test case',
  'cases.newTitle': 'New test case',
  'cases.editTitle': 'Edit {key}',
  'cases.searchPlaceholder': 'Search by title or key',
  'cases.searchLabel': 'Search test cases',
  'cases.filterPriority': 'Filter by priority',
  'cases.filterStatus': 'Filter by status',
  'cases.allPriorities': 'All priorities',
  'cases.allStatuses': 'All statuses',
  'cases.sortLabel': 'Sort test cases',
  'cases.sortNewest': 'Newest first',
  'cases.sortOldest': 'Oldest first',
  'cases.sortTitleAsc': 'Title A→Z',
  'cases.sortTitleDesc': 'Title Z→A',
  'cases.sortUpdated': 'Recently updated',
  'cases.columnKey': 'Key',
  'cases.columns': 'Columns',
  'cases.resetColumns': 'Reset columns',
  'cases.empty': 'No test cases yet',
  'cases.emptyHint': 'Describe your first scenario with steps and expected results.',
  'cases.emptyFiltered': 'No test cases match your filters',
  'cases.emptyFilteredHint': 'Try another search term, or clear the folder and filters.',
  'cases.backToList': 'Back to list',
  'cases.basicInformation': 'Basic information',
  'cases.titlePlaceholder': 'Login with valid credentials',
  'cases.preconditions': 'Precondition',
  'cases.preconditionsHint': 'What must be true before the steps can be executed.',
  'cases.steps': 'Steps',
  'cases.stepsCount': 'Steps ({count})',
  'cases.stepsHint': 'Each step has an action, an expected result and an optional screenshot.',
  'cases.stepsEmpty': 'No steps yet. Add the first step of the scenario.',
  'cases.noSteps': 'This test case has no steps yet.',
  'cases.step': 'Step {number}',
  'cases.stepAction': 'Action',
  'cases.stepData': 'Test data',
  'cases.stepExpected': 'Expected result',
  'cases.stepActionPlaceholder': 'Open the login page',
  'cases.stepDataPlaceholder': 'user@example.com / Passw0rd!',
  'cases.stepExpectedPlaceholder': 'The login form is displayed',
  'cases.automated': 'Automated',
  'cases.testDataHint': 'Accounts, cards or other data the whole scenario needs.',
  'cases.stepScreenshot': 'Screenshot',
  'cases.addScreenshot': 'Add screenshot',
  'cases.addStepScreenshot': 'Add a screenshot to step {number}',
  'cases.removeStepScreenshot': 'Remove the screenshot of step {number}',
  'cases.columnLabels': 'Labels',
  'cases.addStep': 'Add step',
  'cases.moveStepUp': 'Move step {number} up',
  'cases.moveStepDown': 'Move step {number} down',
  'cases.removeStep': 'Remove step {number}',
  'cases.contentTitle': 'Description and data',
  'cases.details': 'Details',
  'cases.created': '{key} created',
  'cases.saved': '{key} saved',
  'cases.duplicated': 'Duplicated as {key}',
  'cases.deleted': '{key} deleted',
  'cases.saveFailed': 'Could not save the test case',
  'cases.duplicateFailed': 'Could not duplicate the test case',
  'cases.deleteFailed': 'Could not delete the test case',
  'cases.deleteTitle': 'Delete {key}?',
  'cases.deleteHint':
    'The test case will no longer appear in this project. Test runs that already recorded results are not affected.',
  'cases.deleteAction': 'Delete test case',
  'cases.moveTitle': 'Move {key}',
  'cases.moveAction': 'Move to folder',
  'cases.folderPlaceholder': 'Choose a folder',
  'cases.moved': '{key} moved',
  'cases.moveFailed': 'Could not move the test case',
  'cases.move': 'Move',

  // ─── checklists ───────────────────────────────────────────────────────────
  'checklists.title': 'Checklists',
  'checklists.subtitle': 'Lightweight lists of checks. A test run is always started from one.',
  'checklists.new': 'New checklist',
  'checklists.create': 'Create checklist',
  'checklists.editTitle': 'Edit checklist',
  'checklists.formHint': 'A short list of checks you can turn into a test run.',
  'checklists.titlePlaceholder': 'Login Smoke',
  'checklists.searchPlaceholder': 'Search by title or key',
  'checklists.searchLabel': 'Search checklists',
  'checklists.columnItems': 'Items',
  'checklists.columnRuns': 'Runs',
  'checklists.empty': 'No checklists yet',
  'checklists.emptyHint': 'Create a checklist, add the checks, and run it whenever you need.',
  'checklists.emptyFiltered': 'No checklists match your search',
  'checklists.emptyFilteredHint': 'Try a different search term.',
  'checklists.created': 'Checklist {key} created',
  'checklists.updated': 'Checklist updated',
  'checklists.duplicated': 'Duplicated as {key}',
  'checklists.deleted': 'Checklist deleted',
  'checklists.saveFailed': 'Could not save the checklist',
  'checklists.duplicateFailed': 'Could not duplicate the checklist',
  'checklists.deleteFailed': 'Could not delete the checklist',
  'checklists.deleteAction': 'Delete checklist',
  'checklists.deleteTitle': 'Delete "{title}"?',
  'checklists.deleteHint': 'The checklist and its items will be deleted.',
  'checklists.deleteHintRuns':
    '{runs} started from this checklist keep their own results and stay untouched.',
  'checklists.editItems': 'Edit items',
  'checklists.itemLinkedCase': 'Test case',
  'checklists.linkCase': 'Link a test case',
  'checklists.linkCaseTitle': 'Link a test case',
  'checklists.linkCaseEmpty': 'No test case matches this search',
  'checklists.unlinkCase': 'Remove the link to {key}',
  'checklists.itemCaseAria': 'Test case of item {number}',
  'checklists.detailsTitle': 'Details',
  'checklists.itemsTitle': 'Items',
  'checklists.itemTitleLabel': 'Check',
  'checklists.itemExpectedLabel': 'Expected result',
  'checklists.saveItems': 'Save items',
  'checklists.itemsSaved': 'Checklist items saved',
  'checklists.itemsSaveFailed': 'Could not save the checklist items',
  'checklists.itemsEmptyEditable': 'No items yet. Add the first check of this checklist.',
  'checklists.itemsEmptyReadonly': 'This checklist has no items yet.',
  'checklists.addItem': 'Add item',
  'checklists.itemTitle': 'Check title',
  'checklists.itemExpected': 'Expected result (optional)',
  'checklists.itemTitleAria': 'Item {number} title',
  'checklists.itemExpectedAria': 'Item {number} expected result',
  'checklists.moveItemUp': 'Move item {number} up',
  'checklists.moveItemDown': 'Move item {number} down',
  'checklists.removeItem': 'Remove item {number}',
  'checklists.discardTitle': 'Discard unsaved changes?',
  'checklists.discardHint': 'The item list will go back to the last saved version.',
  'checklists.discardConfirm': 'Discard changes',
  'checklists.countChecklists': { one: '{count} checklist', other: '{count} checklists' } as Message,
  'checklists.countItems': { one: '{count} item', other: '{count} items' } as Message,

  // ─── test runs ────────────────────────────────────────────────────────────
  'runs.title': 'Test runs',
  'runs.subtitle':
    "Every execution is kept with its own results — this is the project's test history.",
  'runs.new': 'New test run',
  'runs.create': 'Create test run',
  'runs.createShort': 'Create run',
  'runs.formHint':
    'The run takes its own copy of the checklist, so later edits to the checklist never change its results.',
  'runs.titlePlaceholder': 'Regression — Release 2.4',
  'runs.searchPlaceholder': 'Search by title or key',
  'runs.searchLabel': 'Search test runs',
  'runs.filterStatus': 'Filter by status',
  'runs.filterAssignee': 'Filter by assignee',
  'runs.allStatuses': 'All statuses',
  'runs.allAssignees': 'All assignees',
  'runs.columnRun': 'Run',
  'runs.columnChecklist': 'Checklist',
  'runs.columnEnvironment': 'Environment',
  'runs.columnAssignee': 'Assignee',
  'runs.columnResults': 'Result',
  'runs.resultsPassed': '{passed} / {total} passed',
  'runs.resultsFailed': '{count} failed',
  'runs.empty': 'No test runs yet',
  'runs.emptyHint': 'Pick a checklist and start your first run.',
  'runs.emptyFiltered': 'No test runs match your filters',
  'runs.emptyFilteredHint': 'Try another search term or clear the status filter.',
  'runs.fieldChecklist': 'Checklist',
  'runs.fieldChecklistPlaceholder': 'Select a checklist…',
  'runs.fieldChecklistOption': '{key} — {title} ({count})',
  'runs.noRunnableChecklist': 'No checklist with items yet — add items first.',
  'runs.fieldEnvironment': 'Environment',
  'runs.environmentPlaceholder': 'Staging',
  'runs.fieldAssignee': 'Assignee',
  'runs.fieldCommentHint': 'Build number, ticket, anything worth noting.',
  'runs.created': '{key} created',
  'runs.createFailed': 'Could not create the test run',
  'runs.updated': 'Test run updated',
  'runs.updateFailed': 'Could not update the test run',
  'runs.deleted': '{key} deleted',
  'runs.deleteFailed': 'Could not delete the test run',
  'runs.deleteTitle': 'Delete {key}?',
  'runs.deleteHint':
    'This run and the results recorded in it will be removed. Only runs that are still active can be deleted.',
  'runs.deleteAction': 'Delete run',
  'runs.editTitle': 'Edit {key}',
  'runs.backToRuns': 'Back to runs',
  'runs.rerun': 'Re-run',
  'runs.rerunCreated': '{key} created — {source} keeps its results',
  'runs.rerunCreatedShort': '{key} created from the same checklist',
  'runs.rerunFailed': 'Could not start a re-run',
  'runs.complete': 'Complete run',
  'runs.completed': '{key} completed',
  'runs.completeFailed': 'Could not complete the run',
  'runs.completeTitle': 'Complete {key}?',
  'runs.completeHint':
    'Completing the run locks it from further changes. You can reopen it later if needed.',
  'runs.completeHintRemaining':
    '{checks} are still not run. Completing the run locks it — you can reopen it later if needed.',
  'runs.reopen': 'Reopen run',
  'runs.reopened': '{key} reopened — recorded results are kept',
  'runs.reopenFailed': 'Could not reopen the run',
  'runs.checklistDeleted': '{title} (deleted)',
  'runs.sourceChecklists': {
    one: 'Checklist',
    other: 'Checklists ({count})',
  } as Message,
  'runs.sourceChecklistsHint': 'What this run was assembled from, in the order chosen.',
  'runs.contentTitle': 'Description and comment',
  'runs.columnLabels': 'Labels',
  'runs.checklistsChosen': { one: '{count} checklist', other: '{count} checklists' } as Message,
  'runs.removeChecklist': 'Remove {title} from this run',
  'runs.fieldChecklists': 'Checklists',
  'runs.fieldChecklistsHint': 'A run can cover several — the checks follow in the order chosen.',
  'runs.pickAtLeastOneChecklist': 'Choose at least one checklist',
  'runs.metaChecklist': 'Checklist',
  'runs.metaEnvironment': 'Environment',
  'runs.metaAssignee': 'Assignee',
  'runs.metaCreated': 'Created',
  'runs.metaCompleted': 'Completed',
  'runs.runProgress': 'Run progress',
  'runs.progressSummary': '{progress}% · {executed}/{total}',
  'runs.statPassRate': 'Pass rate',
  'runs.statPassed': 'Passed',
  'runs.statFailed': 'Failed',
  'runs.statBlocked': 'Blocked',
  'runs.statSkipped': 'Skipped',
  'runs.statNotRun': 'Not run',
  'runs.statTotal': 'Total',
  'runs.failedChecks': 'Failed checks',
  'runs.checksCount': 'Checks ({count})',
  'runs.checksAria': 'Checks',
  'runs.checksHintEditable':
    'Pick a check and record its result. Keyboard: ↑ / ↓ to move between checks, ← / → to choose the result.',
  'runs.checksHintNoPermission': 'You do not have permission to record results in this run.',
  'runs.checksHintCompleted': 'This run is completed — reopen it to record more results.',
  'runs.skipRemaining': 'Skip remaining ({count})',
  'runs.skipped': '{checks} marked as skipped',
  'runs.skipFailed': 'Could not update the remaining checks',
  'runs.currentCheck': 'Current check — {position} of {total}',
  'runs.currentCheckAria': 'Current check',
  'runs.expectedLabel': 'Expected',
  'runs.result': 'Result',
  'runs.resultFailed': 'Could not record the result',
  'runs.commentPlaceholder': 'What happened? Saved together with the result.',
  'runs.commentAria': 'Comment for check {position}',
  'runs.saveComment': 'Save comment',
  'runs.previousCheck': 'Previous check',
  'runs.nextCheck': 'Next check',
  'runs.hasComment': 'Has a comment',
  'runs.history': 'History',
  'runs.historyTitle': 'History — check {position}',
  'runs.historyEmpty': 'This check has not been executed yet.',
  'runs.historyBy': 'by {name}',
  'runs.executedBy': '{name} · {date}',
  'runs.countChecks': { one: '{count} check', other: '{count} checks' } as Message,

  // ─── search ───────────────────────────────────────────────────────────────
  'search.title': 'Search {project}',
  'search.hint': 'Find test cases, checklists, test runs and folders by title or key.',
  'search.placeholder': 'Type at least one character…',
  'search.termLabel': 'Search term',
  'search.prompt': 'Search by title, or paste a key like {caseKey} or {runKey}.',
  'search.nothing': 'Nothing found for “{term}”.',
  'search.groupCases': 'Test cases',
  'search.groupChecklists': 'Checklists',
  'search.groupRuns': 'Test runs',
  'search.groupFolders': 'Folders',
  'search.openInCases': 'Open in test cases',
  'search.openInChecklists': 'Open in checklists',

  // ─── users ────────────────────────────────────────────────────────────────
  'users.title': 'Users',
  'users.subtitle': 'People sign in through GitLab. An admin changes the role: find the person by GitLab login, or grant access before their first sign-in.',
  'users.new': 'Grant access',
  'users.create': 'Grant access',
  'users.editTitle': 'Edit {login}',
  'users.showInactive': 'Show deactivated accounts',
  'users.searchPlaceholder': 'GitLab login or name',
  'users.searchLabel': 'Search users',
  'users.emptySearchHint': 'Not signed in yet? Grant access by GitLab login — the role applies at their first sign-in.',
  'users.grantTo': 'Grant access to {login}',
  'users.nameHint': 'Optional: taken from GitLab at the first sign-in.',
  'users.columnLogin': 'Login',
  'users.columnRole': 'Role',
  'users.you': 'You',
  'users.active': 'Active',
  'users.deactivated': 'Deactivated',
  'users.empty': 'No users found',
  'users.editUser': 'Edit user',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.deleteUser': 'Delete user',
  'users.fieldRole': 'Role',
  'users.fieldGitlabLogin': 'GitLab username',
  'users.gitlabLoginHint': 'They sign in through GitLab under this username and get this role.',
  'users.created': 'Access granted to {login}',
  'users.updated': 'User updated',
  'users.activated': '{login} reactivated',
  'users.deactivated2': '{login} deactivated',
  'users.deleted': '{login} deleted',
  'users.saveFailed': 'Could not save the user',
  'users.updateFailed': 'Could not update the user',
  'users.deleteFailed': 'Could not delete the user',
  'users.deleteTitle': 'Delete {login}?',
  'users.deleteHint':
    'The account can no longer sign in and its sessions end immediately. Test cases and runs they created keep their author name.',

  // ─── field names used inside validation messages ─────────────────────────
  'field.login': 'Login',
  'field.name': 'Name',
  'field.title': 'Title',
  'field.code': 'Code',
  'field.action': 'Action',
  'field.value': 'Value',

  // ─── messages the API can return ─────────────────────────────────────────
  'server.required': '{field} is required',
  'server.requiredPlain': 'Required',
  'server.invalidId': 'Invalid identifier',
  'server.maxLength': 'Must be at most {max} characters',
  'server.minLength': 'Must be at least {min} characters',
  'server.codeFormat': 'Code must be uppercase letters and digits, starting with a letter',
  'server.loginFormat': 'Login may contain letters, digits, dot, underscore and hyphen',
  'server.nothingToUpdate': 'Nothing to update',
  'server.selectAtLeastOneCase': 'Select at least one test case',
  'server.selectAtLeastOneItem': 'Select at least one item',
  'server.authRequired': 'Authentication required',
  'server.crossSite': 'Cross-site requests are not allowed',
  'server.onlyAuthorOrLead': 'Only the author or a QA Lead can do this',
  'server.validationFailed': 'Request validation failed',
  'server.invalidRequest': 'The request could not be processed',
  'server.parentFolderMissing': 'Parent folder does not exist',
  'server.folderNotInProject': 'Folder does not exist in this project',
  'server.environmentNotInProject': 'Environment does not exist in this project',
  'server.environmentNameTaken': 'An environment with this name already exists',
  'server.layerNotInProject': 'There is no such layer in this project',
  'server.layerNameTaken': 'A layer with this name already exists',
  'server.moduleNotInProject': 'There is no such module in this project',
  'server.moduleNameTaken': 'A module with this name already exists',
  'server.folderDepth': 'Folders can be nested up to {max} levels deep',
  'server.maxDepthReached': 'Maximum nesting depth reached',
  'server.folderNameTaken': 'A folder with this name already exists here',
  'server.folderSelfMove': 'A folder cannot be moved inside itself',
  'server.checklistNotInProject': 'Checklist does not exist in this project',
  'server.checklistNeedsItems': 'Add at least one checklist item first',
  'server.checklistEmpty': 'This checklist has no items to run',
  'server.nothingToRerun': 'There is nothing to re-run: the checklist has no items',
  'server.assigneeMissing': 'Assignee does not exist',
  'server.loginTaken': 'This user already exists — find them in the list and change the role',
  'server.codeTaken': 'This code is already taken',
  'server.projectCodeTaken': 'Project code {code} is already used by "{name}"',
  'server.runAlreadyCompletedExecute':
    'This run is already completed. Reopen it or start a new run to record results.',
  'server.runAlreadyCompleted': 'This run is already completed',
  'server.runAlreadyActive': 'This run is already active',
  'server.runCompletedNoDelete':
    'A completed test run cannot be deleted — it is part of the history',
  'server.lastAdminDemote': 'The last active admin cannot be demoted or deactivated',
  'server.lastAdminDelete': 'The last active admin cannot be deleted',
  'server.selfDeactivate': 'You cannot deactivate your own account',
  'server.selfDelete': 'You cannot delete your own account',
  'server.duplicateRecord': 'A record with these values already exists',
  'server.referencedRecord': 'Referenced record does not exist or is still in use',
  'server.constraintViolation': 'Value violates a database constraint',

  // ─── "X not found", one per entity so every language reads naturally ─────
  'entity.notFound.project': 'Project not found',
  'entity.notFound.folder': 'Folder not found',
  'entity.notFound.environment': 'Environment not found',
  'entity.notFound.testCase': 'Test case not found',
  'entity.notFound.checklist': 'Checklist not found',
  'entity.notFound.testRun': 'Test run not found',
  'entity.notFound.runItem': 'Run item not found',
  'entity.notFound.user': 'User not found',
  'entity.notFound.endpoint': 'Endpoint not found',
  'entity.notFound.resource': 'Resource not found',
  // ─── Labels ──────────────────────────────────────────────────────────────
  'labels.title': 'Labels',
  'labels.hint': 'Shared across the project and assignable to any test case or checklist.',
  'labels.none': 'No labels',
  'labels.empty': 'No labels in this project yet',
  'labels.new': 'New label',
  'labels.newPlaceholder': 'regression',
  'labels.create': 'Create label',
  'labels.add': 'Add label',
  'labels.allLabels': 'All labels',
  'labels.alreadyExists': 'This label already exists',
  'labels.color': 'Colour',
  'labels.editTitle': 'Edit label',
  'labels.actionsFor': 'Actions for the label {name}',
  'labels.deleteTitle': 'Delete the label {name}?',
  'labels.deleteHint':
    'It will be taken off {cases} test cases and {checklists} checklists. Those are kept.',
  'labels.notUsed': 'not used yet',
  'labels.usage': 'cases: {cases} · checklists: {checklists}',
  'labels.created': 'Label {name} created',
  'labels.updated': 'Label {name} saved',
  'labels.deleted': 'Label {name} deleted',
  'labels.saveFailed': 'Could not save the label',
  'labels.createFailed': 'Could not create the label',
  'labels.deleteFailed': 'Could not delete the label',

  // ─── Environments ────────────────────────────────────────────────────────
  'environments.title': 'Environments',
  'environments.hint': 'The places this project is tested on — a test case, a checklist and a run each point at one.',
  'environments.empty': 'No environments in this project yet',
  'environments.emptyHint': 'Add one in the project settings to choose it here.',
  'environments.none': 'No environment',
  'environments.new': 'New environment',
  'environments.newPlaceholder': 'Staging',
  'environments.create': 'Create environment',
  'environments.editTitle': 'Edit environment',
  'environments.actionsFor': 'Actions for the environment {name}',
  'environments.deleteTitle': 'Delete the environment {name}?',
  'environments.deleteHint':
    'It will be cleared from {cases} test cases, {checklists} checklists and {runs} runs. Those are kept.',
  'environments.notUsed': 'not used yet',
  'environments.usage': 'cases: {cases} · checklists: {checklists} · runs: {runs}',
  'environments.created': 'Environment {name} created',
  'environments.updated': 'Environment {name} saved',
  'environments.deleted': 'Environment {name} deleted',
  'environments.saveFailed': 'Could not save the environment',
  'environments.deleteFailed': 'Could not delete the environment',

  // ─── Layers and modules ──────────────────────────────────────────────────
  'layers.title': 'Layers',
  'layers.hint':
    'What a test case or a checklist exercises — the UI, the API — chosen from this list.',
  'layers.empty': 'No layers in this project yet',
  'layers.none': 'No layer',
  'layers.new': 'New layer',
  'layers.newPlaceholder': 'UI',
  'layers.create': 'Create layer',
  'layers.editTitle': 'Edit layer',
  'layers.actionsFor': 'Actions for the layer {name}',
  'layers.deleteTitle': 'Delete the layer {name}?',
  'layers.deleteHint':
    'It will be cleared from {cases} test cases and {checklists} checklists. Those are kept.',
  'layers.notUsed': 'not used yet',
  'layers.usage': 'cases: {cases} · checklists: {checklists}',
  'layers.created': 'Layer {name} created',
  'layers.updated': 'Layer {name} saved',
  'layers.deleted': 'Layer {name} deleted',
  'layers.saveFailed': 'Could not save the layer',
  'layers.deleteFailed': 'Could not delete the layer',

  'modules.title': 'Modules',
  'modules.hint':
    'The parts of the product this project tests — a test case and a checklist name one of them.',
  'modules.empty': 'No modules in this project yet',
  'modules.none': 'No module',
  'modules.new': 'New module',
  'modules.newPlaceholder': 'Payments',
  'modules.create': 'Create module',
  'modules.editTitle': 'Edit module',
  'modules.actionsFor': 'Actions for the module {name}',
  'modules.deleteTitle': 'Delete the module {name}?',
  'modules.deleteHint':
    'It will be cleared from {cases} test cases and {checklists} checklists. Those are kept.',
  'modules.notUsed': 'not used yet',
  'modules.usage': 'cases: {cases} · checklists: {checklists}',
  'modules.created': 'Module {name} created',
  'modules.updated': 'Module {name} saved',
  'modules.deleted': 'Module {name} deleted',
  'modules.saveFailed': 'Could not save the module',
  'modules.deleteFailed': 'Could not delete the module',

  // ─── Rich text ───────────────────────────────────────────────────────────
  'richText.bold': 'Bold',
  'richText.italic': 'Italic',
  'richText.heading': 'Heading',
  'richText.bulletList': 'Bulleted list',
  'richText.numberedList': 'Numbered list',
  'richText.inlineCode': 'Inline code',
  'richText.codeBlock': 'Code block',
  'richText.insertImage': 'Insert a screenshot',
  'richText.preview': 'Preview',
  'richText.edit': 'Edit',
  'richText.nothingToPreview': 'Nothing to preview yet.',
  'richText.placeholder':
    'Markdown is supported: **bold**, lists, ``` for code. Paste a screenshot to attach it.',
  'richText.screenshot': 'Screenshot',
  'richText.openScreenshot': 'Open {name} at full size',
  'richText.imageTooLarge': 'The image is larger than 5 MB',
  'richText.uploadFailed': 'Could not upload the image',
} as const;

export type MessageKey = keyof typeof en;
export type Dictionary = Record<MessageKey, Message>;
